import { alertController, loadingController, modalController, toastController } from '@ionic/core';
import { Component, Event, EventEmitter, forceUpdate, Fragment, h, Host, Prop, State } from '@stencil/core';
import { CostCode, DateUtils, PersonDoc, ProjectDoc, RfiDoc, RFISettingsDoc, TagData, TaskDoc, TaskRelationship, Workflow, WorkflowState } from 'common';
import { PeopleService } from 'src/global/service/PeopleService';
import { ScheduleService } from 'src/global/service/ScheduleService';
import { DateSelector } from 'src/global/ui/date-selector';
import { RfiService } from 'src/RFIs/services/RfiService';
import { RfiUtils, RfiWorkflowUtils } from '..';
import { ConcoursePersonSelector } from '../../components/concourse-person-selector/concourse-person-selector';
import { Calendar } from '../../components/concourse-schedule/model/Calendar';
import { TaskSelector } from '../../components/concourse-schedule/task-selector';
import { ConcourseTagSelector } from '../../components/concourse-tag-selector/concourse-tag-selector';
import { ChangeEvent, ConcourseDb, DbListener, peopleState, TimelineUtils } from '../../global';
import { User } from '../../global/models';
import { ProjectService } from '../../projects/services/ProjectService';
import { CostCodeSelector } from '../../contracts/components/cost-code-selector';
import { WorkflowStateModal } from 'src/submittals';

@Component({
  tag: 'rfi-create-modal',
})
export class RfiCreateModal {
  static async present(projectId: string, rfi: RfiDoc): Promise<RfiDoc | null> {
    const modal = await modalController.create({
      component: 'rfi-create-modal',
      componentProps: { original: rfi, projectId },
      backdropDismiss: false,
      cssClass: 'modal-height-auto',
    });
    return new Promise(async resolve => {
      modal.addEventListener('saved', (e: CustomEvent<RfiDoc>) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      await modal.present();
    });
  }

  @Prop() original: RfiDoc;
  @Prop() projectId: string;
  @Event() saved: EventEmitter<RfiDoc>;
  @Event() dismiss: EventEmitter<void>;

  @State() rfi: RfiDoc;
  private project: ProjectDoc;
  private workflows: Workflow[];

  @State() scheduleTasks: TaskDoc[] = [];
  @State() settings: RFISettingsDoc = RfiUtils.DEFAULT_RFI_SETTINGS;

  @State() errorMessages: string;
  @State() calendar: Calendar;
  @State() personDueBySelectorOpen: boolean = false;

  @Event({ bubbles: true }) inviteUser;

  private notify: boolean = true;

  get user(): User {
    return globalThis.user;
  }

  get secondStep(): WorkflowState {
    return this.rfi?.states[1];
  }

  get isDraft(): boolean {
    return RfiUtils.isDraft(this.rfi);
  }

  get isEditMode(): boolean {
    return !this.isDraft;
  }

  get people(): PersonDoc[] {
    return peopleState.people;
  }

  async componentWillLoad() {
    // Deep copy to avoid mutating the original until save
    this.rfi = JSON.parse(JSON.stringify(this.original));


    const settingsDb = RfiService.getRfiSettingsDb(this.projectId);
    settingsDb.addListeners(this.settingsDbListener);
    await settingsDb.syncRemote();

    this.project = await ProjectService.getProjectDoc(this.projectId);

    const workflows = this.project.rfis_workflows || [];
    workflows.unshift(RfiUtils.DEFAULT_RFI_WORKFLOW);
    this.workflows = workflows;

    if (this.rfi?.successors && this.rfi?.successors?.length > 0) {
      this.loadScheduleTasks();
    }

    this.calendar = await ScheduleService.getCalendarOrDefault(this.projectId, this.project.default_calendar_unique_id);
  }

  private settingsDbListener: DbListener = {
    onComplete: (docs: RFISettingsDoc[]) => (this.settings = docs[0]),
    onChange: (event: ChangeEvent) => (this.settings = event.docs[0]),
    onError: (error: Error) => console.error(error),
  };

  async loadScheduleTasks() {
    const db = ScheduleService.getTasksDb(this.projectId);
    db.addListeners(this.tasksDbListener);
    await db.syncRemote();
  }

  private tasksDbListener: DbListener = {
    onComplete: (docs: TaskDoc[]) => (this.scheduleTasks = docs),
    onChange: (event: ChangeEvent) => (this.scheduleTasks = event.docs),
    onError: (error: Error) => console.error(error),
  };

  disconnectedCallback() {
    const db = ScheduleService.getTasksDb(this.projectId);
    db.removeListeners(this.tasksDbListener);
  }

  hasField(name: string) {
    return !!this.settings?.fields?.includes(name);
  }

  hasOneField(names: string[]) {
    return !!names.find(name => this.settings?.fields?.includes(name));
  }

  async showDateSelector() {
    DateSelector.presentModal(this.rfi.due_date, (date: string) => {
      this.rfi.due_date = date;
      forceUpdate(this);
    });
  }

  removeDueByPerson(personId: string) {
    this.secondStep.dueBy = this.secondStep?.dueBy.filter(id => id !== personId);
    forceUpdate(this);
  }

  async addDueByPerson() {
    const people = await ConcoursePersonSelector.present({
      projectId: this.projectId,
      feature: 'rfis',
      featureId: this.rfi._id,
      peopleToOmitIds: this.rfi.distribution,
      needsWrite: true,
    });
    if (people && people.length > 0) {
      this.secondStep.dueBy = [...this.secondStep.dueBy, ...people.map(p => p._id)];
      forceUpdate(this);
    }
  }
  async onBarClick(index: number) {
    const state: WorkflowState = {
      id: null,
      dueBy: [],
      dueDays: 0,
      action: '',
      pastTense: '',
    };
    const updated = await WorkflowStateModal.present(this.projectId, state, 'rfis', this.rfi._id, false);
    if (updated && (updated as WorkflowState)) {
      const newState: WorkflowState = { ...(updated as WorkflowState), id: ConcourseDb.makeShortDocId() };
      this.rfi.states.splice(index + 1, 0, newState);
      forceUpdate(this);
    }
  }

  async onBubbleClick(indexes: number[]) {
    const stateIndex = indexes[0];
    if (stateIndex == 0) {
      alertController
        .create({
          header: 'Cannot edit create state',
          message: 'The create state cannot be edited',
          buttons: ['OK'],
        })
        .then(alert => alert.present());
      return;
    }
    const state = this.rfi.states[stateIndex];
    const updated = await WorkflowStateModal.present(this.projectId, state, 'rfis', this.rfi._id, true);
    if (updated && (updated as WorkflowState)) {
      this.rfi.states[stateIndex] = updated as WorkflowState;
      forceUpdate(this);
    }
    if (updated && typeof updated === 'string') {
      this.rfi.states = this.rfi.states.filter((s, idx) => idx !== stateIndex);
      forceUpdate(this);
    }
  }

  async showDistributionPersonSelector() {
    const people = await ConcoursePersonSelector.present({ projectId: this.projectId, feature: 'rfis', featureId: this.rfi._id, peopleToOmitIds: this.rfi.distribution });
    if (people && people.length > 0) {
      this.rfi.distribution = [...this.rfi.distribution, ...people.map(p => p._id)];
      forceUpdate(this);
    }
  }

  removeDistributionPerson(personId: string) {
    this.rfi.distribution = this.rfi.distribution.filter(id => id !== personId);
    forceUpdate(this);
  }

  async showTagSelector() {
    ConcourseTagSelector.presentModal(this.projectId, null, async (tag: TagData) => {
      this.rfi = RfiUtils.addTag(this.rfi, tag);
      if (this.isEditMode) {
        this.rfi = await RfiService.saveRfi(this.projectId, this.rfi);
      }
      forceUpdate(this);
    });
  }

  async removeTag(tag: TagData) {
    this.rfi = RfiUtils.removeTag(this.rfi, tag);
    if (this.isEditMode) {
      this.rfi = await RfiService.saveRfi(this.projectId, this.rfi);
    }
    forceUpdate(this);
  }

  async showScheduleSelector() {
    const task = await TaskSelector.presentModal(this.projectId);
    if (task) {
      RfiUtils.addTask(this.rfi, task.unique_id, 'FS', 0);

      // If the tasks were not loaded on componentWillLoad, we load them now.
      if (!this.scheduleTasks || this.scheduleTasks.length == 0) {
        this.loadScheduleTasks();
      }
      forceUpdate(this);
    }
  }

  removeTask(task_unique_id: number) {
    RfiUtils.removeTask(this.rfi, task_unique_id);
    forceUpdate(this);
  }

  async showCostCodeSelector() {
    CostCodeSelector.present(this.projectId, async (costCode: CostCode) => {
      if (costCode) {
        this.rfi = { ...this.rfi, cost_code: costCode };
        forceUpdate(this);
      }
    });
  }

  changeWorkflow(event: CustomEvent<Workflow | null>) {
    const workflow = event?.detail;
    if (!workflow) {
      return;
    }
    this.rfi = { ...this.rfi, states: workflow.states };
  }

  /*
  onWorkflowChange(workflowId: string) {
    const workflow = this.workflows.find(w => w.id == workflowId);
    if (workflow) {
      this.rfi.workflow = workflow;
    }
    forceUpdate(this);
  }
  async showAttachmentSelector() {
    FilePicker.pickFiles().then(async (result) => {
      if (result.files.length > 0) {
        const file:PickedFile = result.files[0];
        const base64 = await blobToBase64(file.blob);
        //await this.rfiDetail.addAttachment(file.name, file.mimeType, base64, this.user);
        forceUpdate(this);
      }
    });
  }*/
  /*
    async removeAttachment(filename) {
      await alertController
        .create({
          header: 'Are you sure',
          message: 'You will not be able to recover this file. Are you sure you want to delete it?',
          buttons: [
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Confirm',
              role: 'confirm',
              handler: async () => {
                this.rfiDetail.clearAttachment(filename, this.user);
                forceUpdate(this);
  
                await toastController
                  .create({
                    message: 'The attachment has been removed',
                    duration: 5000,
                  })
                  .then(toast => toast.present());
              },
            },
          ],
        })
        .then(alert => alert.present());
    }
  */
  async saveDraft() {
    const savedRfi = await RfiService.saveDraft(this.projectId, this.rfi);
    await toastController
      .create({
        message: 'Draft saved',
        duration: 5000,
      })
      .then(toast => toast.present());
    this.saved.emit(savedRfi);
  }

  getValidationError(): string {
    if (!this.rfi.code) return 'Please specify a number for the RFI';
    if (!this.rfi.subject) return 'Please specify a subject for the RFI';
    if (!this.rfi.description) return 'Please give the RFI a description';
    if (!this.rfi.states || this.rfi.states.length === 0) return 'Please specify a workflow for the RFI';
    if (!this.secondStep.dueBy || this.secondStep.dueBy.length === 0) return 'Please choose at least one assignee';
    if (this.hasField('question') && !this.rfi.question) return 'Please give the RFI a question';
    if (this.hasField('suggestedAnswer') && !this.rfi.suggested_answer) return 'Please provide a suggested solution';
  }

  formatAlphanumeric(e: CustomEvent) {
    const input = e.target as HTMLInputElement;
    this.rfi.code = input.value.replace(/[^a-zA-Z0-9-_]+/g, '');
    forceUpdate(this);
  }

  async createRfi() {
    this.errorMessages = this.getValidationError();
    if (this.errorMessages) return false;

    const loading = await loadingController.create({ message: 'Creating RFI...' });
    try {
      loading.present();
      const rfi = await RfiService.createRfi(this.projectId, this.rfi);
      loading.message = 'RFI created';
      setTimeout(() => {
        loading.dismiss();

        this.saved.emit(rfi);
      }, 1000);
    } catch (error) {
      loading.dismiss();
      alertController.create({ message: 'There was an error creating the RFI: ' + error.message }).then(a => a.present());
    }
  }

  async updateRfi() {
    try {
      const savedRfi = await RfiService.updateRfi(this.projectId, this.rfi);
      this.saved.emit(savedRfi);
    } catch (error) {
      console.error('Error saving rfi:', error);
      // Show error to user
      const alert = await alertController.create({
        header: 'Error',
        message: `Failed to save rfi: ${error.message}`,
        buttons: ['OK'],
      });
      await alert.present();
    }
  }

  get assigneesDesc(): string {
    const dueBy: string[] = this.secondStep.dueBy;
    return dueBy.length == 1 ? '1 assignee' : `${dueBy.length} assignees`;
  }

  render() {
    const modalTitle = this.rfi._rev ? (this.isDraft ? 'Edit Draft' : 'Edit RFI') : 'Create RFI';
    const rfiStateIds = this.rfi.states?.map(s => s.id).join(',') || '';
    const workflow = this.workflows.find(w => w.states.map(s => s.id).join(',') == rfiStateIds);
    return (
      <Host class="concourse-modal">
        <modal-header modalTitle={modalTitle} onDismiss={() => this.dismiss.emit()}>
          {this.isDraft && this.secondStep && this.secondStep?.dueBy?.length > 0 && (
            <ion-checkbox class="ion-margin-end" checked={this.notify} onIonChange={e => (this.notify = e.detail.checked)}>
              Notify {this.assigneesDesc}
            </ion-checkbox>
          )}
        </modal-header>
        <div class="modal-content cc-scrollbars">
          <ion-grid>
            <ion-row>
              <ion-col size="6">
                <ion-input
                  id="rfi-num-input"
                  label="# Number"
                  label-placement="stacked"
                  value={this.rfi.code}
                  onIonChange={(e: CustomEvent) => this.formatAlphanumeric(e)}
                  helper-text="Only numbers, letters, hyphens (-) and underscores (_) are allowed"
                ></ion-input>
              </ion-col>
              <ion-col size="6">
                <date-picker-input
                  selectedDate={this.rfi.due_date}
                  inputProps={{
                    clearInput: true,
                    labelPlacement: 'stacked',
                    label: 'Due Date (optional)',
                    name: 'due_date',
                    value: this.rfi.due_date ? new Date(this.rfi.due_date).toLocaleDateString() : '',
                  }}
                  onIonChange={event => {
                    this.rfi = { ...this.rfi, due_date: event.detail.detail.value };
                  }}
                ></date-picker-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="12">
                <ion-input
                  id="rfi-subject-input"
                  label="Subject"
                  label-placement="stacked"
                  value={this.rfi.subject}
                  onIonChange={e => (this.rfi = { ...this.rfi, subject: e.target.value })}
                ></ion-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="12">
                <ion-label class="typography-body2-400" style={{ color: 'var(--background-color-step-950)' }}>
                  Description
                </ion-label>
              </ion-col>
              <ion-col size="12">
                <concourse-textarea
                  data-testid="rfi-description-input"
                  label="Description (optional)"
                  class="cc-margin-bottom"
                  rows="5"
                  projectId={this.projectId}
                  value={this.rfi.description}
                  onUpdated={(e: CustomEvent) => (this.rfi.description = e.detail)}
                  onPersonSelected={(e: CustomEvent) => RfiUtils.addDistribution(this.rfi, e.detail, this.user.id)}
                  onTagReferenced={(e: CustomEvent) => RfiUtils.addTag(this.rfi, e.detail)}
                  placeholder="For best results:
o Provide background information and context.
o Reference drawings, photos, and contract documents.
o Ask a clear question and suggest a reasonable response."
                />
              </ion-col>
            </ion-row>
            {this.isDraft && (
              <ion-row>
                <ion-col size="12">
                  <select-input
                    inputProps={{
                      label: 'Workflow',
                      labelPlacement: 'stacked',
                      placeholder: 'Select workflow',
                      value: workflow?.name || '',
                    }}
                    selectProps={{
                      required: true,
                      value: workflow,
                    }}
                    onSelectChange={(event: CustomEvent) => this.changeWorkflow(event)}
                  >
                    {this.workflows.map(workflow => (
                      <ion-select-option value={workflow}>{workflow.name}</ion-select-option>
                    ))}
                  </select-input>
                </ion-col>
                <ion-col>
                  {this.rfi.states && this.calendar && (
                    <concourse-timeline
                      bubbles={TimelineUtils.getTimelineBubbles(this.rfi.status_history, this.rfi.states, this.people, this.calendar)}
                      activeIndex={RfiWorkflowUtils.getLastCompletedStateIdx(this.rfi) + 1}
                      onBubbleClick={e => this.onBubbleClick(e.detail)}
                      onBarClick={e => this.onBarClick(e.detail)}
                      dueDate={DateUtils.fromISO(this.rfi.due_date)}
                    />
                  )}
                </ion-col>
              </ion-row>
            )}
            {this.hasField('question') && (
              <ion-row>
                <ion-col size="12">
                  <ion-label class="typography-body2-400" style={{ color: 'var(--background-color-step-950)' }}>
                    Question
                  </ion-label>
                </ion-col>
                <ion-col size="12">
                  <concourse-textarea
                    label="Question"
                    rows="4"
                    class="cc-margin-bottom"
                    projectId={this.projectId}
                    value={this.rfi.question}
                    onUpdated={(e: CustomEvent) => (this.rfi.question = e.detail)}
                    onPersonSelected={(e: CustomEvent) => RfiUtils.addDistribution(this.rfi, e.detail, this.user.id)}
                    onTagReferenced={(e: CustomEvent) => RfiUtils.addTag(this.rfi, e.detail)}
                    placeholder="Please phrase the question."
                  />
                </ion-col>
              </ion-row>
            )}
            {this.hasField('suggestedAnswer') && (
              <ion-row>
                <ion-col size="12">
                  <ion-label class="typography-body2-400" style={{ color: 'var(--background-color-step-950)' }}>
                    Suggested Answer
                  </ion-label>
                </ion-col>
                <ion-col size="12">
                  <concourse-textarea
                    label="Suggested Answer"
                    rows="4"
                    projectId={this.projectId}
                    value={this.rfi.suggested_answer}
                    onUpdated={(e: CustomEvent) => (this.rfi.suggested_answer = e.detail)}
                    onPersonSelected={(e: CustomEvent) => RfiUtils.addDistribution(this.rfi, e.detail, this.user.id)}
                    onTagReferenced={(e: CustomEvent) => RfiUtils.addTag(this.rfi, e.detail)}
                    placeholder="Please propose a solution."
                  />
                </ion-col>
              </ion-row>
            )}
            {this.secondStep && this.isDraft && (
              <ion-row>
                <ion-col>
                  <chip-list-section sectionTitle="Assigned to" addClick={() => this.addDueByPerson()}>
                    {this.secondStep.dueBy.map(id => (
                      <person-chip person={PeopleService.findPerson(this.people, id)} removable={true} onRemove={e => this.removeDueByPerson(id)} />
                    ))}
                  </chip-list-section>
                </ion-col>
              </ion-row>
            )}
            {this.hasField('distribution') && (
              <ion-row>
                <ion-col>
                  <chip-list-section sectionTitle="Distribution (optional)" addClick={() => this.showDistributionPersonSelector()}>
                    {this.rfi.distribution.map(id => (
                      <person-chip person={PeopleService.findPerson(this.people, id)} removable={true} onRemove={e => this.removeDistributionPerson(id)} />
                    ))}
                  </chip-list-section>
                </ion-col>
              </ion-row>
            )}
          

          {this.hasField('tasks') && (
            <ion-row>
              <ion-col>
                <chip-list-section sectionTitle="Tasks (optional)" addClick={() => this.showScheduleSelector()}>
                  {this.rfi?.successors?.map((relationship: TaskRelationship) => {
                    const task = this.scheduleTasks && this.scheduleTasks.length > 0 ? this.scheduleTasks.find(t => t.unique_id == relationship.task_unique_id) : null;
                    return (
                      <ion-chip class="chip">
                        <ion-label>{task?.name || relationship.task_unique_id}</ion-label>
                        <ion-icon
                          name="close-circle"
                          onClick={e => {
                            e.stopPropagation();
                            this.removeTask(relationship.task_unique_id);
                          }}
                        ></ion-icon>
                      </ion-chip>
                    );
                  })}
                </chip-list-section>
              </ion-col>
            </ion-row>
          )}

          {this.hasField('tags') && (
            <ion-row>
              <ion-col>
                <chip-list-section sectionTitle="Tags (optional)" addClick={() => this.showTagSelector()}>
                  {this.rfi.tags.map((tag: TagData) => (
                    <ion-chip class={!!tag.unique_id ? 'chip schedule-tag' : 'chip project-tag'}>
                      <ion-label>
                        {'#' + tag.name}
                        {tag.desription && '- ' + tag.desription}
                      </ion-label>
                      <ion-icon
                        name="close-circle"
                        onClick={e => {
                          e.stopPropagation();
                          this.removeTag(tag);
                        }}
                      ></ion-icon>
                    </ion-chip>
                  ))}
                </chip-list-section>
              </ion-col>
            </ion-row>
          )}
          </ion-grid>

          {/* {this.hasField('attachments') && (
                    <ion-row>
                      <ion-col size="12">
                        <ion-label class="typography-body2-400" style={{ marginBottom: '6px', color: 'var(--background-color-step-950)' }}>
                          Attachments
                        </ion-label>
                      </ion-col>

                      <ion-col style={{ width: '100%' }}>
                        {this.rfi.attachments?.map(file => {
                                              return (
                                                <div style={{ maxWidth: '40%', overflow: 'hidden' }}>
                                                  <file-preview-card key={file._id} file={file} showDelete={true} onDelete={() => this.removeFile(file)} />;
                                                </div>
                                              );
                                            })}
                                            {this.uploads.map((file, i) => (
                                              <file-uploader-instance
                                                style={{ width: '100%' }}
                                                data-filename={file.name}
                                                projectId={this.projectId}
                                                fileType={FileType.DOCUMENT}
                                                file={file}
                                                fileRoles={SubmittalUtils.getReadRoles(this.projectId, this.submittal._id)}
                                                onUploaded={e => this.onUploaded(e.detail)}
                                              />
                                            ))}
                                            <concourse-file-uploader style={{ width: '100%' }} multiple={true} onFilesSelected={event => this.onUploadSelected(event.detail)} />}
                      </ion-col>
                    </ion-row>
                  )} */}

          {this.hasOneField(['priority', 'cost-code', 'cost-impact', 'schedule-impact']) && (
            <Fragment>
              <ion-item class="">
                <ion-label class="typography-body1-500">Priority & Impact</ion-label>
              </ion-item>
              <ion-grid>
                {this.hasField('priority') && (
                  <ion-row>
                    <ion-col size="12">
                      <ion-input label="Priority (optional)" label-placement="stacked" value={this.rfi.priority} onIonChange={e => (this.rfi.priority = e.target.value)} />
                    </ion-col>
                  </ion-row>
                )}
                {this.hasField('cost-code') && (
                  <ion-row>
                    <ion-col size="12">
                      <div onClick={() => this.showCostCodeSelector()}>
                        <ion-input
                          label="Cost Code (optional)"
                          label-placement="stacked"
                          placeholder="Select Cost Code"
                          value={this.rfi.cost_code ? `${this.rfi.cost_code.code_number} - ${this.rfi.cost_code.division_name}` : ''}
                          readonly
                        >
                          <ion-icon slot="end" name="chevron-down-outline" class="input-icon" aria-hidden="true"></ion-icon>
                        </ion-input>
                      </div>
                    </ion-col>
                  </ion-row>
                )}
                {this.hasField('cost-impact') && (
                  <ion-row>
                    <ion-col size="12">
                      <ion-input label="Cost Impact (optional)" label-placement="stacked" value={this.rfi.cost_impact} onIonChange={e => (this.rfi.cost_impact = e.target.value)} />
                    </ion-col>
                  </ion-row>
                )}
                {this.hasField('schedule-impact') && (
                  <ion-row>
                    <ion-col size="12">
                      <ion-input
                        label="Schedule Impact (optional)"
                        label-placement="stacked"
                        value={this.rfi.schedule_impact}
                        onIonChange={e => (this.rfi.schedule_impact = e.target.value)}
                      />
                    </ion-col>
                  </ion-row>
                )}
              </ion-grid>
            </Fragment>
          )}
        </div>
        <ion-footer>
          {this.errorMessages && <ion-text color="danger">{this.errorMessages}</ion-text>}
          <ion-toolbar>
            {this.isDraft ? (
              [
                <ion-button slot="start" color="light" fill="outline" shape="round" onClick={() => this.saveDraft()}>
                  Save Draft
                </ion-button>,
                <ion-button slot="end" data-testid="create-rfi-button" fill="solid" shape="round" onClick={() => this.createRfi()}>
                  Create RFI
                </ion-button>,
              ]
            ) : (
              <ion-button slot="end" fill="solid" shape="round" onClick={() => this.updateRfi()}>
                Update RFI
              </ion-button>
            )}
          </ion-toolbar>
        </ion-footer>
      </Host>
    );
  }
}
